import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { addToCart } from "../../Redux/CartSlice";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { Context } from "../../Context/ContextApi";

const PaintingDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cartSlice.cartItems);

  // State for product options
  const [selectedSize, setSelectedSize] = useState("");
  const [productDetail, setProductDetail] = useState(null);
  const [print, setPrint] = useState("");

  const { pathname } = useLocation();
  const url = pathname?.split("/")[2];

  // --- Helper to determine the current price (Memoized) ---
  const getCurrentPrice = useMemo(() => {
    if (!productDetail) return null;
    
    // If dynamic sizes are present and a size is selected, find its price
    if (productDetail.sizes?.length > 0 && selectedSize) {
      const sizeData = productDetail.sizes.find(
        (data) => data.title === selectedSize
      );
      // Return the price from the selected size. Use base price if size data is corrupt.
      if (sizeData && sizeData.price !== undefined) {
        return sizeData.price;
      }
    }
    
    // If no dynamic sizes, or if data is corrupt, return the base product price
    return productDetail.product_price;
  }, [productDetail, selectedSize]);


  // --- Data Fetching Effect ---
  useEffect(() => {
    // Reset states when URL changes
    setProductDetail(null);
    setSelectedSize("");
    setPrint("");

    if (url) {
      axios
        .get(`https://admin.tulikaarts.in/api/product-detail/${url}`)
        .then((response) => {
          const product = response.data?.product;
          setProductDetail(product);
          
          // Pre-select the first size if available for better UX
          if (product?.sizes?.length > 0) {
            setSelectedSize(product.sizes[0].title);
          }
        })
        .catch((error) => console.error("Error fetching product detail:", error));
    }
  }, [url]);

  // --- Dynamic Price Handler ---
  const handleSizeChange = (e) => {
    const newSize = e.target.value;
    setSelectedSize(newSize);
    // getCurrentPrice useMemo hook will automatically recalculate the price here
  };

  // --- Cart Item Builder ---
  const createPaintingObject = () => ({
    id: productDetail?.id,
    title: productDetail?.product_name,
    description: productDetail?.short_description,
    size: selectedSize,
    print: print,
    price: getCurrentPrice, // Use the real-time calculated price
    image: `https://admin.tulikaarts.in/${productDetail?.product_image}`,
    quantity: 1,
  });

  // --- Cart/Buy Handlers ---
  const handleAddToCart = () => {
    // 1. Validation for required fields
    if (!print) {
      toast.error("Please select a 'Print On' option.", { duration: 2000 });
      return;
    }

    const isSizeRequired = productDetail?.sizes?.length > 0;
    if (isSizeRequired && !selectedSize) {
      toast.error("Please select a size.", { duration: 2000 });
      return;
    }
    
    const painting = createPaintingObject();

    // Final price check
    if (painting.price === null || painting.price === undefined) {
        toast.error("Price error: Cannot add to cart. Please refresh.", { duration: 2000 });
        return;
    }

    // 2. Check if item (by ID) is already in cart
    const isAdded = cartItems?.find((data) => data.id === painting.id);

    if (!isAdded) {
      dispatch(addToCart(painting));
      toast.success("Added to cart", { duration: 2000 });
    } else {
      toast.error("Already added", { duration: 1500 });
    }
  };

  const handleBuyNow = () => {
    // Attempt to add to cart; if successful, navigate
    handleAddToCart(); 
    
    // Check if validation would pass
    const isSizeRequired = productDetail?.sizes?.length > 0;
    const isValid = print && (!isSizeRequired || selectedSize);
    
    // Only navigate if validation passed AND price is valid
    if (isValid && getCurrentPrice !== null && getCurrentPrice !== undefined) {
      navigate("/cart");
    }
  };

  // Display loading or empty state if productDetail is null
  if (!productDetail) {
    return (
      <div className="container py-5 text-center" style={{ marginTop: "80px" }}>
        Loading product details...
      </div>
    );
  }

  // Determine button disabled state
  const isSizeRequired = productDetail.sizes?.length > 0;
  // Button is disabled if print is missing OR (size is required and missing) OR price couldn't be determined
  const isButtonDisabled = !print || (isSizeRequired && !selectedSize) || getCurrentPrice === null || getCurrentPrice === undefined;
  
  // Use getCurrentPrice for the main display
  const displayPrice = getCurrentPrice;

  return (
    <div className="container py-5" style={{ marginTop: "80px" }}>
      <div className="row align-items-start">
        {/* Image Section */}
        <div className="col-md-6 mb-4">
          <div
            className="shadow-sm rounded-4 overflow-hidden border"
            style={{ background: "#fff" }}
          >
            <img
              src={`https://admin.tulikaarts.in/${productDetail.product_image}`} 
              alt={productDetail.product_name}
              width="100%"
              className="img-fluid"
            />
          </div>
        </div>

        {/* Details Section */}
        <div className="col-md-6">
          <div
            className="p-4 rounded-4 shadow-sm border bg-white"
            style={{ lineHeight: "1.8" }}
          >
            <h2 className="fw-bold mb-3 text-dark">
              {productDetail.product_name}
            </h2>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="text-success fw-semibold">
                {/* Check and display price */}
                ₹ {displayPrice !== null && displayPrice !== undefined ? displayPrice : productDetail.product_price}
              </h4>
              <span className="text-secondary">
                {/* Show product_size only if dynamic sizes are NOT required */}
                {!isSizeRequired && <b>Size:</b>} {productDetail.product_size}
              </span>
            </div>

            <hr />

            {/* Print On Selection */}
            <div className="mb-3">
              <h5 className="fw-bold mb-2 text-dark">Print On: <span className="text-danger">*</span></h5>
              <div className="d-flex flex-wrap gap-3">
                <div className="form-check">
                  <input
                    type="radio"
                    name="print"
                    value="Canvas"
                    checked={print === "Canvas"}
                    onChange={(e) => setPrint(e.target.value)}
                    className="form-check-input"
                    id="canvas"
                  />
                  <label className="form-check-label" htmlFor="canvas">
                    Canvas
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    name="print"
                    value="Archival Paper"
                    checked={print === "Archival Paper"}
                    onChange={(e) => setPrint(e.target.value)}
                    className="form-check-input"
                    id="archival"
                  />
                  <label className="form-check-label" htmlFor="archival">
                    Archival Paper
                  </label>
                </div>
              </div>
            </div>

            {/* Select Size Selection */}
            {isSizeRequired && (
              <div className="mb-4">
                <h5 className="fw-bold mb-2 text-dark">Select Size: <span className="text-danger">*</span></h5>
                <div className="d-flex flex-wrap gap-3">
                  {productDetail.sizes.map((data, index) => (
                    <div key={index} className="form-check">
                      <input
                        type="radio"
                        name="size"
                        value={data.title}
                        checked={selectedSize === data.title}
                        onChange={handleSizeChange}
                        className="form-check-input"
                        id={`size-${index}`}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`size-${index}`}
                      >
                        {data.title} (₹{data.price}) {/* This displays the price correctly in the option */}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <hr />

            <p className="text-muted">{productDetail.short_description}</p>

            <div className="mt-4 d-flex gap-3">
              <button
                onClick={handleBuyNow}
                className="btn btn-dark px-4 py-2 rounded-pill"
                disabled={isButtonDisabled}
              >
                Buy Now
              </button>
              <button
                onClick={handleAddToCart}
                className="btn btn-outline-dark px-4 py-2 rounded-pill"
                disabled={isButtonDisabled}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaintingDetails;